import { Fragment, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import { PHASE_COLORS, PHASE_LABEL, PHASE_ORDER } from "./offer-phase-tokens";
import { OfferLineRow } from "./OfferLineRow";
import { OfferLineCard } from "./OfferLineCard";
import { OFFER_LINE_GRID } from "./offer-line-nav";
import { SrIcon } from "@/components/ui/SrIcon";
import { Textarea } from "@/components/ui/textarea";
import { calcSectionTotals, type OfferForCalc, type OfferLineForCalc, type OfferPhase } from "@/lib/offer-calc";
import type { OfferLine } from "@/hooks/use-offers";
import type { OfferSection } from "@/hooks/use-offer-sections";

export interface SectionGroup {
  /** null = hovedbudsjett. */
  section: OfferSection | null;
  lines: OfferLine[];
  subtotal: number;
}

interface SectionBlockProps {
  group: SectionGroup;
  /**
   * Read-only-modus (ikke-forfatter/ikke-admin). Deaktiverer rad-inputs,
   * skjuler "legg til"/slett-seksjon-knapper og låser terms-textarea.
   * VIB-offer-author-gating.
   */
  readOnly?: boolean;
  hourlyRate: number;
  /** Katalogens timesats per linje; default `hourlyRate` (VIB-2097). */
  rateForLine?: (line: OfferLine) => number;
  /** Katalogens default overtidssats (kr/t) per linje (VIB-2087). */
  overtimeRateForLine?: (line: OfferLine) => number;
  autoFocusLineId?: string | null;
  copyingSectionKey?: string | null;
  dragId: string | null;
  dropTargetId: string | null;
  copyDrag: boolean;
  dragHandlersFor: (id: string) => React.HTMLAttributes<HTMLDivElement>;
  /** Offer-level overhead/discount — anvendes per seksjon. */
  offerTotals: OfferForCalc;
  /** Lagret terms-text for denne seksjonen (main → offers.custom_terms_text). */
  termsText: string;
  onTermsTextChange: (text: string) => void;
  onChange: (id: string, patch: Partial<OfferLine>) => void;
  onDelete: (id: string) => void;
  onDuplicate?: (sourceId: string, targetId: string | null) => void;
  onAddLine: (sectionId: string | null) => void;
  onDeleteSection: (sectionId: string) => void;
  onCopySectionImage: (sectionId: string | null) => void;
  /** Mobile tap → åpne edit-sheet. Sendes opp til OfferEditPage. */
  onLineTap: (lineId: string) => void;
  /** Mobile reorder via opp/ned-knapp. Bytter sort_order med nabolinje. */
  onMoveLine: (lineId: string, dir: "up" | "down") => void;
}

export function OfferSectionBlock({
  group,
  readOnly = false,
  hourlyRate,
  rateForLine,
  overtimeRateForLine,
  autoFocusLineId,
  copyingSectionKey,
  dragId,
  dropTargetId,
  copyDrag,
  dragHandlersFor,
  offerTotals,
  termsText,
  onTermsTextChange,
  onChange,
  onDelete,
  onDuplicate,
  onAddLine,
  onDeleteSection,
  onCopySectionImage,
  onLineTap,
  onMoveLine,
}: SectionBlockProps) {
  const { section, lines: groupLines, subtotal } = group;
  const isOption = section !== null;
  const title = section?.name ?? "Hovedbudsjett";
  const sectionKey = section?.id ?? null;
  // "main"-sentinel matcher hovedbudsjettet uten å bruke null som verdi-key.
  const myKey: string = section?.id ?? "main";
  const isCopying = copyingSectionKey === myKey;
  const isAnyCopying = !!copyingSectionKey;

  const phaseMap = useMemo(() => {
    const map: Record<OfferPhase, OfferLine[]> = {
      pl: [],
      preprod: [],
      production: [],
      postprod: [],
      stills: [],
      reise: [],
    };
    for (const l of groupLines) if (map[l.phase]) map[l.phase].push(l);
    return map;
  }, [groupLines]);

  const visiblePhases = PHASE_ORDER.filter((p) => phaseMap[p].length > 0);
  const lastPhase = visiblePhases[visiblePhases.length - 1];

  // VIB-700: Per-seksjon totaler med overhead/rabatt for tilvalg.
  // Hovedbudsjettet beholder enkel subtotal i headeren.
  const sectionTotals = isOption
    ? calcSectionTotals(
        offerTotals,
        groupLines.map((l): OfferLineForCalc => ({
          phase: l.phase,
          cost_type: l.cost_type,
          pricing_type: l.pricing_type,
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          purchase_price: l.purchase_price,
          amount: l.amount,
          overtime_split: l.overtime_split ?? null,
        })),
      )
    : null;

  // Lokal state for textarea slik at vi kun lagrer onBlur (debounce-aktig).
  const [localTerms, setLocalTerms] = useState(termsText);
  // Sync når parent endrer (f.eks. annen bruker eller invalidation).
  useEffect(() => setLocalTerms(termsText), [termsText]);

  return (
    <div
      data-section-id={section?.id ?? "main"}
      className={cn(
        "border rounded-md overflow-hidden bg-background",
        isOption && "border-accent/40 bg-accent/[0.02]",
      )}
    >
      {/* Section-header */}
      <div
        className={cn(
          "flex items-center justify-between px-3 py-1.5 border-b",
          isOption ? "bg-accent/10" : "bg-muted/40",
        )}
      >
        <div className="flex items-center gap-2">
          {isOption && (
            <span
              className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/30 text-accent-foreground"
              aria-hidden
            >
              Tilvalg
            </span>
          )}
          <h2 className="font-display text-sm font-semibold">{title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {isOption && sectionTotals ? (
            <div className="flex items-baseline gap-3 text-xs">
              <span className="text-muted-foreground">
                Sum <span className="text-foreground tabular-nums">{formatNOK(sectionTotals.subtotal)}</span>
              </span>
              {sectionTotals.overhead_amount > 0 && (
                <span className="text-muted-foreground">
                  + <span className="text-foreground tabular-nums">{formatNOK(sectionTotals.overhead_amount)}</span> OH
                </span>
              )}
              {sectionTotals.discount_amount > 0 && (
                <span className="text-destructive tabular-nums">
                  − {formatNOK(sectionTotals.discount_amount)}
                </span>
              )}
              <span className="font-display font-semibold tabular-nums text-sm pl-2 border-l border-border">
                {formatNOK(sectionTotals.total)}
              </span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatNOK(subtotal)}
            </span>
          )}
          <button
            type="button"
            onClick={() => onCopySectionImage(sectionKey)}
            disabled={isAnyCopying || groupLines.length === 0}
            className={cn(
              "text-[11px] hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 rounded",
              "text-muted-foreground hover:text-foreground hover:bg-muted",
              "disabled:opacity-40 disabled:hover:bg-transparent",
              "transition-colors",
            )}
            aria-label={`Kopier ${title} som bilde`}
            title={
              groupLines.length === 0
                ? "Legg til linjer først"
                : `Kopier ${title} som bilde`
            }
          >
            {isCopying ? (
              <SrIcon name="loader2" className="w-3 h-3 animate-spin" />
            ) : (
              <SrIcon name="image" className="w-3 h-3" />
            )}
            Kopier bilde
          </button>
          {isOption && section && !readOnly && (
            <button
              type="button"
              onClick={() => onDeleteSection(section.id)}
              className="text-muted-foreground/50 hover:text-destructive transition-colors"
              aria-label={`Fjern tilvalg ${title}`}
              title="Fjern tilvalg (linjer flyttes til hovedbudsjett)"
            >
              <SrIcon name="x" className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Kolonneoverskrifter (kun desktop — mobil viser kort) */}
      {groupLines.length > 0 && (
        <div
          className={cn(
            "hidden md:grid items-center px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80",
            OFFER_LINE_GRID,
            "bg-muted/30 border-b",
          )}
        >
          <span aria-hidden />
          <span className="pl-1">Linje</span>
          <span className="text-right pr-1.5">Mengde</span>
          <span className="pl-1">Enhet</span>
          <span className="text-right pr-1.5">Sats</span>
          <span className="text-right pr-1.5">Innkjøp</span>
          <span className="text-right pr-3">Beløp</span>
          <span aria-hidden />
        </div>
      )}

      {groupLines.length > 0 ? (
        <div className="px-3">
          {visiblePhases.map((phase) => {
            const faseLines = phaseMap[phase];
            const color = PHASE_COLORS[phase];
            return (
              <div
                key={phase}
                className={phase !== visiblePhases[0] ? "border-t border-border/50" : undefined}
              >
                <div className="flex items-baseline gap-2 px-1 pt-1 pb-0.5">
                  <span
                    className="w-1.5 h-1.5 rounded-sm"
                    style={{ background: color }}
                    aria-hidden
                  />
                  <h3 className="font-display text-xs text-muted-foreground">{PHASE_LABEL[phase]}</h3>
                </div>

                {/* Mobile-stack: kort med tap + flytt-knapper */}
                <div className="md:hidden space-y-2 pb-2">
                  {faseLines.map((line, i) => (
                    <OfferLineCard
                      key={line.id}
                      line={line}
                      isFirst={i === 0}
                      isLast={i === faseLines.length - 1}
                      isLocked={(line.is_auto_generated ?? false) || readOnly}
                      onTap={() => onLineTap(line.id)}
                      onMoveUp={() => onMoveLine(line.id, "up")}
                      onMoveDown={() => onMoveLine(line.id, "down")}
                      onDuplicate={
                        onDuplicate ? () => onDuplicate(line.id, null) : undefined
                      }
                    />
                  ))}
                </div>

                {/* Desktop-rader: tabell-layout med drag-handle */}
                <div className="hidden md:block">
                  {faseLines.map((line, i) => {
                    const isLastOverall = phase === lastPhase && i === faseLines.length - 1;
                    return (
                      <Fragment key={line.id}>
                        <OfferLineRow
                          line={line}
                          hourlyRate={rateForLine ? rateForLine(line) : hourlyRate}
                          defaultOvertimeRate={
                            overtimeRateForLine ? overtimeRateForLine(line) : 0
                          }
                          readOnly={readOnly}
                          striped={i % 2 === 1}
                          autoFocus={line.id === autoFocusLineId}
                          dragHandlers={dragHandlersFor(line.id)}
                          isDragging={dragId === line.id}
                          isDropTarget={dropTargetId === line.id}
                          isCopyDrop={copyDrag && dropTargetId === line.id}
                          onChange={(patch) => onChange(line.id, patch)}
                          onDelete={() => onDelete(line.id)}
                          onDuplicate={
                            onDuplicate ? () => onDuplicate(line.id, null) : undefined
                          }
                          onTabOut={isLastOverall ? () => onAddLine(sectionKey) : undefined}
                        />
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-3 py-4 text-center text-xs text-muted-foreground">
          Ingen linjer ennå
        </div>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={() => onAddLine(sectionKey)}
          className={cn(
            "w-full text-left px-3 py-1.5 text-xs font-medium",
            "text-primary border-t border-dashed hover:bg-muted/30 transition-colors",
          )}
        >
          + Legg til produkt
          <span className="ml-2 text-muted-foreground font-normal">
            {isOption
              ? `Søk i katalogen og legg til en linje i ${title}`
              : "eller ⌘L for å søke i katalogen · Tab/Enter på siste rad gir ny linje"}
          </span>
        </button>
      )}

      {/* VIB-700: inline tekst-rubrikk per seksjon */}
      <div className="border-t bg-muted/5 px-3 py-2">
        <label className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          Tekst på {isOption ? "tilvalg" : "tilbud"}
        </label>
        <Textarea
          value={localTerms}
          onChange={(e) => setLocalTerms(e.target.value)}
          onBlur={() => {
            if (localTerms !== termsText) onTermsTextChange(localTerms);
          }}
          readOnly={readOnly}
          placeholder="F.eks. forbehold, leveringsdato, eller egne notater til kunden…"
          rows={2}
          className="text-sm resize-y"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          Vises i kopier-bilde og Keynote. La stå tomt for standardteksten fra
          tilbudsmalen.
        </p>
      </div>
    </div>
  );
}
